import React from 'react';
import { Grid, Paper, Stack, Chip, Typography, Divider, Button, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import FilterDropdown from './FilterDropdown';
import { COLOR_PRIMARY_2 } from 'src/colors';

// Define the shape of options used in filters
interface FilterOption {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
}

// Define the shape of a grouped filter
interface GroupedFilter {
  type: string;
  label: string;
  values: string[];
  handler: (values: string[]) => void; // Function to clear the filter
  options: FilterOption[];
}

// Define common button styles (can be shared or defined locally)
const commonButtonSx = {
  justifyContent: 'flex-start',
  boxShadow: 'none',
  height: '36.5px', // Match typical Button height
  minWidth: 'auto', // Adjust as needed
};
const outlinedButtonSx = {
  borderColor: 'divider', // Use divider color for subtle border
  color: 'text.primary',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'action.hover',
    borderColor: 'divider',
  },
};

// Define the props for the SelectedFiltersDisplay component
interface SelectedFiltersDisplayProps {
  activeGroupedFilters: GroupedFilter[];
  findLabel: (options: FilterOption[], value: string) => string;
  selectedSortBy: string;
  sortByOptions: FilterOption[];
  handleSortByChange: (value: string | string[]) => void;
  isMobileScreen: boolean;
}

const SelectedFiltersDisplay: React.FC<SelectedFiltersDisplayProps> = ({
  activeGroupedFilters,
  findLabel,
  selectedSortBy,
  sortByOptions,
  handleSortByChange,
  isMobileScreen,
}) => {
  const hasActiveGroupedFilters = activeGroupedFilters.length > 0;
  const selectedSortByLabel = findLabel(sortByOptions, selectedSortBy) || 'Sort By';
  const hasAnyActiveContent = hasActiveGroupedFilters || selectedSortBy;

  if (!hasAnyActiveContent) {
    return null; // Don't render if nothing is selected/sorted
  }

  return (
    <Grid item xs={12} sx={{ mt: 0 }}>
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {hasActiveGroupedFilters && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 'medium', color: 'text.secondary', mr: 1, flexShrink: 0 }}
              >
                Filters:
              </Typography>
            )}
            {activeGroupedFilters.map(group => {
              const chipLabelContent = (
                <>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 'bold' }}>
                    {`${group.label}: `}
                  </Typography>
                  {group.values.map((value, index) => {
                    const label = findLabel(group.options, value);
                    return (
                      <Typography component="span" variant="body2" key={value}>
                        {label}
                        {index < group.values.length - 1 ? (
                          <Typography component="span" variant="body1" sx={{ fontWeight: 800 }}>
                            {', '}
                          </Typography>
                        ) : (
                          ''
                        )}
                      </Typography>
                    );
                  })}
                </>
              );
              return (
                <Chip
                  key={group.type}
                  label={chipLabelContent}
                  size="small"
                  onDelete={() => group.handler([])}
                />
              );
            })}
          </Box>

          <FilterDropdown
            label="Sort By"
            button={
              <Button
                variant={'outlined'}
                startIcon={<SortIcon />}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  ...commonButtonSx,
                  ...outlinedButtonSx,
                  minWidth: '180px',
                  justifyContent: 'space-between',
                }}
                disableElevation
              >
                <Typography
                  variant="button"
                  component="span"
                  sx={{ textAlign: 'left', flexGrow: 1, mr: 1 }}
                >
                  {selectedSortByLabel}
                </Typography>
              </Button>
            }
            options={sortByOptions}
            value={selectedSortBy}
            onChange={handleSortByChange}
            singleSelect
            hideSearch
            extraStyle={isMobileScreen ? { marginLeft: 'auto !important', marginTop: '10px !important' } : {}}
          />
        </Stack>
      </Paper>
    </Grid>
  );
};

export default SelectedFiltersDisplay;
